import React, { useState, useEffect } from "react";
import axios from "axios";
import {
    Box,
    Typography,
    CircularProgress,
    Alert,
    TextField,
    MenuItem,
    Grid,
    Button,
    LinearProgress,
    Table,
    TableHead,
    TableRow,
    TableCell,
    TableBody,
    TableContainer,
    Paper
} from "@mui/material";

const InstrumentList = () => {
    const [instruments, setInstruments] = useState([]);
    const [loading, setLoading] = useState(false);
    const [error, setError] = useState("");
    const [hasMore, setHasMore] = useState(true);
    const [filters, setFilters] = useState({ broker: "", exchange: "", instrument_type: "", symbol: "" });
    const [page, setPage] = useState(1);
    const [userRole, setUserRole] = useState(null);
    const [brokers, setBrokers] = useState([]);
    const [selectedBroker, setSelectedBroker] = useState("");
    const [instrumentUrl, setInstrumentUrl] = useState("");
    const [syncing, setSyncing] = useState(false);
    const [syncMessage, setSyncMessage] = useState("");
    const [brokerMap, setBrokerMap] = useState({});
    const API_BASE_URL = process.env.REACT_APP_API_BASE_URL;


    useEffect(() => { fetchInstruments(1, true); }, [filters]);

    useEffect(() => {
        const observer = new IntersectionObserver((entries) => {
            if (entries[0].isIntersecting && hasMore && !loading) {
                fetchInstruments(page + 1);
            }
        }, { threshold: 1 });
        const sentinel = document.getElementById("scroll-sentinel");
        if (sentinel) observer.observe(sentinel);
        return () => observer.disconnect();
    }, [page, hasMore, loading]);

    useEffect(() => {
        const fetchUserAndBrokers = async () => {
            const token = localStorage.getItem("token");
            try {
                const res = await axios.get(`${API_BASE_URL}/users/me`, {
                    headers: { Authorization: `Bearer ${token}` },
                });
                setUserRole(res.data.role);
                const brokersRes = await axios.get(`${API_BASE_URL}/brokers/list-minimal`, {
                    headers: { Authorization: `Bearer ${token}` },
                });
                setBrokers(brokersRes.data);
                const map = {};
                brokersRes.data.forEach((b) => { map[b._id] = b.brokerName; });
                setBrokerMap(map);
            } catch (err) {
                console.error("Failed to load user or brokers info.", err);
            }
        };
        fetchUserAndBrokers();
    }, []);

    const fetchInstruments = async (pageToFetch, reset = false) => {
        setLoading(true);
        try {
            const token = localStorage.getItem("token");
            const res = await axios.get(`${API_BASE_URL}/instruments/list`, {
                headers: { Authorization: `Bearer ${token}` },
                params: { ...filters, page: pageToFetch, limit: 50 },
            });
            const results = Array.isArray(res?.data) ? res.data : [];
            setInstruments(prev => {
                if (reset) return results;
                const existingIds = new Set(prev.map(i => i._id));
                const newUnique = results.filter(i => !existingIds.has(i._id));
                return [...prev, ...newUnique];
            });
            setHasMore(results.length > 0);
            setPage(pageToFetch);
        } catch (err) {
            setError("Failed to fetch instruments");
        } finally {
            setLoading(false);
        }
    };

    const handleFilterChange = (e) => {
        setFilters({ ...filters, [e.target.name]: e.target.value });
    };

    const handleSync = async () => {
        if (!selectedBroker || !instrumentUrl) {
            setSyncMessage("Please select broker and provide instrument URL.");
            return;
        }
        setSyncing(true);
        setSyncMessage("Starting sync...");
        try {
            const token = localStorage.getItem("token");
            const res = await axios.post(`${API_BASE_URL}/instruments/sync-url`, {
                broker: selectedBroker, url: instrumentUrl
            }, { headers: { Authorization: `Bearer ${token}` } });
            setSyncMessage(res.data.message || "Sync initiated successfully.");
        } catch (err) {
            setSyncMessage(err.response?.data?.detail || "Sync failed");
        } finally {
            setSyncing(false);
        }
    };

    return (
        <Box p={3} maxWidth={1200} mx="auto">
            <Typography variant="h5" gutterBottom>Instrument List</Typography>

            <Grid container spacing={2} mb={2}>
                <Grid item xs={3}>
                    <Box sx={{ minWidth: 90, maxWidth: 300, flexGrow: 1 }}>
                        <TextField label="Broker" name="broker" value={filters.broker} onChange={handleFilterChange} select fullWidth><MenuItem value="">All</MenuItem>{brokers.map((b) => (<MenuItem key={b._id} value={b._id}>{b.brokerName}</MenuItem>))}</TextField>
                    </Box>
                </Grid>
                <Grid item xs={3}><TextField label="Exchange" name="exchange" value={filters.exchange} onChange={handleFilterChange} fullWidth /></Grid>
                <Grid item xs={3}><TextField label="Instrument Type" name="instrument_type" value={filters.instrument_type} onChange={handleFilterChange} fullWidth /></Grid>
                <Grid item xs={3}><TextField label="Search Symbol" name="symbol" value={filters.symbol} onChange={handleFilterChange} fullWidth /></Grid>
            </Grid>

            {userRole === "admin" && (
                <Box my={4} p={2} border="1px solid #ccc" borderRadius={2}>
                    <Typography variant="h6">Sync Instruments from URL</Typography>
                    <Grid container spacing={2} alignItems="center" mt={1}>
                        <Grid item xs={3}>
                            <Box sx={{ minWidth: 90, maxWidth: 300, flexGrow: 1 }}>
                                <TextField select label="Broker" value={selectedBroker} onChange={(e) => setSelectedBroker(e.target.value)} fullWidth>{brokers.map((b) => (<MenuItem key={b._id} value={b._id}>{b.brokerName}</MenuItem>))}</TextField>
                            </Box>
                        </Grid>
                        <Grid item xs={6}><TextField label="Instrument List URL" value={instrumentUrl} onChange={(e) => setInstrumentUrl(e.target.value)} fullWidth /></Grid>
                        <Grid item xs={3}><Button variant="contained" onClick={handleSync} disabled={syncing}>{syncing ? "Syncing..." : "Sync Instruments"}</Button></Grid>
                    </Grid>
                    {syncing && <LinearProgress sx={{ mt: 1 }} />}
                    {syncMessage && <Alert sx={{ mt: 2 }} severity="info">{syncMessage}</Alert>}
                </Box>
            )}

            {error && <Alert severity="error">{error}</Alert>}

            <TableContainer component={Paper} sx={{ mt: 2 }}>
                <Table>
                    <TableHead>
                        <TableRow>
                            <TableCell>Sr.No</TableCell>
                            <TableCell>Token</TableCell>
                            <TableCell>Symbol</TableCell>
                            <TableCell>Name</TableCell>
                            <TableCell>Exchange</TableCell>
                            <TableCell>Type</TableCell>
                            <TableCell>Broker</TableCell>
                        </TableRow>
                    </TableHead>
                    <TableBody>
                        {instruments.map((item, index) => (
                            <TableRow key={item._id}>
                                <TableCell>{index + 1}</TableCell>
                                <TableCell>{item.instrument_token || item.token}</TableCell>
                                <TableCell>{item.tradingsymbol || item.symbol}</TableCell>
                                <TableCell>{item.name}</TableCell>
                                <TableCell>{item.exch_seg || item.exchange}</TableCell>
                                <TableCell>{item.instrument_type}</TableCell>
                                <TableCell>{brokerMap[item.broker] || item.broker}</TableCell>
                            </TableRow>
                        ))}
                    </TableBody>
                </Table>
            </TableContainer>

            {loading && <CircularProgress sx={{ mt: 2 }} />}
            <div id="scroll-sentinel" style={{ height: 20 }}></div>
        </Box>
    );
};

export default InstrumentList;
